import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/api';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AdminPagination } from '../../components/AdminPagination';
import { AdminSearchBar } from '../../components/AdminSearchBar';

export const AdminRoutesPage = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [formData, setFormData] = useState({
    from_label: '',
    to_label: '',
    from_place_id: '',
    to_place_id: '',
    is_active: true
  });

  const loadRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getRoutes(page, 20, search);
      setRoutes(response.data.items || []);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error loading routes:', error);
      toast.error('Error al cargar rutas');
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [page, search]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        from_label: formData.from_label,
        to_label: formData.to_label,
        from_place_id: formData.from_place_id || null,
        to_place_id: formData.to_place_id || null,
        is_active: formData.is_active
      };
      
      if (editingRoute) {
        await adminApi.updateRoute(editingRoute.id, data);
        toast.success('Ruta actualizada exitosamente');
      } else {
        await adminApi.createRoute(data);
        toast.success('Ruta creada exitosamente');
      }
      
      setDialogOpen(false);
      setEditingRoute(null);
      resetForm();
      loadRoutes();
    } catch (error) {
      console.error('Error saving route:', error);
      toast.error('Error al guardar la ruta');
    }
  };
  
  const handleEdit = (route) => {
    setEditingRoute(route);
    setFormData({
      from_label: route.from_label,
      to_label: route.to_label,
      from_place_id: route.from_place_id || '',
      to_place_id: route.to_place_id || '',
      is_active: route.is_active
    });
    setDialogOpen(true);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta ruta?')) return;
    
    try {
      await adminApi.deleteRoute(id);
      toast.success('Ruta eliminada exitosamente');
      loadRoutes();
    } catch (error) {
      console.error('Error deleting route:', error);
      toast.error('Error al eliminar la ruta');
    }
  };
  
  const resetForm = () => {
    setFormData({
      from_label: '',
      to_label: '',
      from_place_id: '',
      to_place_id: '',
      is_active: true
    });
  };
  
  if (loading && !hasLoadedRef.current) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Rutas</h1>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar ruta..." />
          <Button
            onClick={() => {
              resetForm();
              setEditingRoute(null);
              setDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 shrink-0"
            data-testid="add-route-btn"
          >
            <Plus className="w-4 h-4" />
            Agregar
          </Button>
        </div>
      </div>
      
      <div className={`bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Desde</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Hasta</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Place IDs</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {routes.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-500">
                  {search ? `No se encontraron rutas para "${search}"` : 'No hay rutas registradas'}
                </td></tr>
              )}
              {routes.map((route) => (
                <tr key={route.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{route.from_label}</p>
                    {route.from_place_id && <p className="text-xs text-slate-500">{route.from_place_id}</p>}
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{route.to_label}</p>
                    {route.to_place_id && <p className="text-xs text-slate-500">{route.to_place_id}</p>}
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {route.from_place_id || route.to_place_id ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400" />
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full
                      ${route.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                      {route.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => handleEdit(route)}
                        className="p-2 hover:bg-slate-100 rounded transition-colors"
                        data-testid={`edit-route-${route.id}`}
                      >
                        <Edit className="w-4 h-4 text-slate-600" />
                      </button>
                      <button 
                        onClick={() => handleDelete(route.id)}
                        className="p-2 hover:bg-red-50 rounded transition-colors"
                        data-testid={`delete-route-${route.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRoute ? 'Editar' : 'Agregar'} Ruta</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Desde *</label>
              <Input 
                value={formData.from_label}
                onChange={(e) => setFormData({...formData, from_label: e.target.value})}
                placeholder="Aeropuerto Internacional de Punta Cana"
                required
                data-testid="from-label-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Hasta *</label>
              <Input 
                value={formData.to_label}
                onChange={(e) => setFormData({...formData, to_label: e.target.value})}
                placeholder="Bávaro - Punta Cana"
                required
                data-testid="to-label-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Place ID - Desde (opcional)</label>
              <Input 
                value={formData.from_place_id}
                onChange={(e) => setFormData({...formData, from_place_id: e.target.value})}
                placeholder="ChIJxxx... (Google Places ID)"
              />
              <p className="text-xs text-slate-500 mt-1">Para integración futura con Google Maps</p>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Place ID - Hasta (opcional)</label>
              <Input 
                value={formData.to_place_id}
                onChange={(e) => setFormData({...formData, to_place_id: e.target.value})}
                placeholder="ChIJxxx... (Google Places ID)"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Ruta activa</label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {editingRoute ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
